'use es6';

import { withTheme } from 'foundations-theming/utils';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { connect } from 'react-redux';
import { Map as ImmutableMap } from 'immutable';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import ContentRoutes from 'ContentUtils/Routes';
import { Button } from 'foundations-components/transitional/components/Button';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { List } from 'foundations-components/transitional/components/List';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { MediaObject } from 'foundations-components/transitional/components/MediaObject';
import { ConfirmModal } from 'foundations-components/transitional/components/ConfirmModal';
import { DeleteIcon, UndoIcon, AddIcon } from 'foundations-assets';
import { Text } from 'foundations-components/components/Text';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { H2 } from 'foundations-components/transitional/components/H2';
import { View } from 'foundations-components/transitional/components/View';
import { getAbActiveVariant, getAbMaster, getHasAbTestStarted, getABTestID } from 'ContentEditorUI/redux/selectors/abTestSelectors';
import { updatePagesAbTest } from 'ContentEditorUI/redux/actions/abTestActions';
import { getAppStatus } from 'ContentEditorUI/redux/selectors/appStatusSelectors';
import { APP_STATUS } from 'ContentEditorUI/redux/constants';
import { getSubcategory } from 'ContentEditorUI/redux/selectors/baseContentModelSelectors';
import { getCategoryId } from 'ContentEditorUI/redux/selectors/contentReadOnlyDataSelectors';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const mapStateToProps = state => {
  return {
    abMaster: getAbMaster(state),
    abVariation: getAbActiveVariant(state),
    hasAbTestStarted: getHasAbTestStarted(state),
    abTestId: getABTestID(state),
    appStatus: getAppStatus(state),
    subcategory: getSubcategory(state),
    categoryId: getCategoryId(state)
  };
};
const mapDispatchToProps = {
  updatePagesAbTest
};
const ErrorLabel = ({
  error
}) => error ? /*#__PURE__*/_jsx(FormattedMessage, {
  message: `abTestManager.validationErrorMessage.${error}`
}) : null;
const ActionIcon = ({
  IconComponent = DeleteIcon,
  onClick,
  tooltip = 'deleteVariation'
}) => /*#__PURE__*/_jsx(IconButton, {
  onClick: onClick,
  tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
    message: `abTestManager.${tooltip}`
  }),
  use: "transparent",
  placement: "right",
  children: /*#__PURE__*/_jsx(IconComponent, {})
});
const VALIDATION_ERRORS = {
  EMPTY_MASTER_NAME: 'emptyMasterVariationName',
  DUPLICATE_VARIATION_NAME: 'hasDuplicateName',
  EMPTY_VARIATION_NAME: 'hasEmptyVariationName'
};
const getVariationError = (variationName, masterVariationName) => {
  if (variationName !== null && !variationName) {
    return VALIDATION_ERRORS.EMPTY_VARIATION_NAME;
  }
  if (variationName === masterVariationName) {
    return VALIDATION_ERRORS.DUPLICATE_VARIATION_NAME;
  }
  return null;
};
class ABTestManagerModalWithoutTheme extends Component {
  constructor(props) {
    super(props);
    this.handleMasterVariationNameChange = ({
      target: {
        value
      }
    }) => {
      this.setState({
        masterVariation: {
          name: value,
          nameChanged: true,
          error: value.length === 0 ? VALIDATION_ERRORS.EMPTY_MASTER_NAME : null
        },
        currentVariation: Object.assign({}, this.state.currentVariation, {
          error: getVariationError(this.state.currentVariation.name, value)
        }),
        newVariation: Object.assign({}, this.state.newVariation, {
          error: getVariationError(this.state.newVariation.name, value)
        })
      });
    };
    this.handleVariationNameChange = ({
      target: {
        value
      }
    }) => {
      this.setState({
        currentVariation: {
          name: value,
          nameChanged: true,
          error: getVariationError(value, this.state.masterVariation.name)
        }
      });
    };
    this.handleNewVariationNameChange = ({
      target: {
        value
      }
    }) => {
      this.setState({
        newVariation: {
          name: value,
          error: getVariationError(value, this.state.masterVariation.name)
        }
      });
    };
    this.handleDeleteCurrentVariation = () => {
      this.setState({
        currentVariation: Object.assign({}, this.state.currentVariation, {
          deleted: true
        })
      });
    };
    this.handleUndoDeleteCurrentVariation = () => {
      this.setState({
        currentVariation: Object.assign({}, this.state.currentVariation, {
          deleted: false
        }),
        newVariation: {
          name: null,
          error: null
        }
      });
    };
    this.handleDeleteNewVariation = () => {
      this.setState({
        newVariation: {
          name: null,
          error: null
        }
      });
    };
    this.handleAddVariation = () => {
      this.setState({
        newVariation: {
          name: I18n.text('abTestManager.newVariationName', {
            masterVariationName: I18n.SafeString(this.state.masterVariation.name)
          }),
          error: null
        }
      });
    };
    this.handleSaveChangesClick = () => {
      if (this.state.currentVariation.deleted) {
        this.setState({
          deleteModalOpen: true
        });
      } else {
        this.saveChanges();
      }
    };
    this.saveChanges = () => {
      const {
        currentVariation: {
          deleted: currentVariationDeleted,
          name: currentVariationName,
          nameChanged: currentVariationNameChanged
        },
        masterVariation: {
          name: masterName,
          nameChanged: masterNameChanged
        },
        newVariation: {
          name: newVariationName
        }
      } = this.state;
      this.props.updatePagesAbTest({
        testId: this.props.abTestId,
        deleteAbTest: currentVariationDeleted,
        createNewTest: currentVariationDeleted && newVariationName,
        renameVariationValue: currentVariationNameChanged ? currentVariationName : undefined,
        renameMasterValue: masterNameChanged ? masterName : undefined,
        newVariationName
      });
    };
    this.state = {
      masterVariation: {
        name: props.abMaster.name,
        nameChanged: false,
        error: null
      },
      currentVariation: {
        name: props.abVariation.name,
        nameChanged: false,
        deleted: false,
        error: null
      },
      newVariation: {
        error: null,
        name: null
      },
      deleteModalOpen: false
    };
  }
  getMessage() {
    const {
      hasAbTestStarted
    } = this.props;
    const {
      deleted,
      name
    } = this.state.currentVariation;
    const messageKeyPostfix = hasAbTestStarted ? 'testWillEnd' : deleted ? 'testWillBeDeleted' : 'default';
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: `abTestManager.deleteModal.message.${messageKeyPostfix}`,
      options: {
        variationName: name
      }
    });
  }
  renderMasterVariationName() {
    const {
      name,
      error
    } = this.state.masterVariation;
    return this.props.hasAbTestStarted ? /*#__PURE__*/_jsx(View, {
      marginBlockEnd: "200",
      as: "span",
      children: name
    }) : /*#__PURE__*/_jsx(FormControl, {
      "aria-label": "Original variation name",
      error: !!error,
      validationMessage: /*#__PURE__*/_jsx(ErrorLabel, {
        error: error
      }),
      marginBlockEnd: "200",
      children: /*#__PURE__*/_jsx(TextInput, {
        onChange: this.handleMasterVariationNameChange,
        value: name
      })
    }, "masterVariation");
  }
  renderCurrentVariation() {
    const {
      error,
      name,
      deleted
    } = this.state.currentVariation;
    const iconProps = deleted ? {
      IconComponent: UndoIcon,
      onClick: this.handleUndoDeleteCurrentVariation,
      tooltip: 'deleteVariationUndo'
    } : {
      onClick: this.handleDeleteCurrentVariation
    };
    return this.props.hasAbTestStarted ? /*#__PURE__*/_jsx(View, {
      marginBlockEnd: "200",
      as: "span",
      children: name
    }) : /*#__PURE__*/_jsx(MediaObject, {
      itemRight: /*#__PURE__*/_jsx(ActionIcon, Object.assign({}, iconProps)),
      marginBlockEnd: "200",
      children: /*#__PURE__*/_jsx(FormControl, {
        "aria-label": "Current variation name",
        error: !!error,
        validationMessage: /*#__PURE__*/_jsx(ErrorLabel, {
          error: error
        }),
        children: /*#__PURE__*/_jsx(Text, {
          as: "div",
          textDecoration: deleted ? 'line-through' : 'none',
          color: deleted ? 'text-core-disabled' : 'text-core-default',
          children: /*#__PURE__*/_jsx(TextInput, {
            readOnly: deleted,
            onChange: this.handleVariationNameChange,
            value: name
          })
        })
      })
    });
  }
  renderNewVariation() {
    const {
      name,
      error
    } = this.state.newVariation;
    if (!name) {
      return null;
    }
    return /*#__PURE__*/_jsx(MediaObject, {
      itemRight: /*#__PURE__*/_jsx(ActionIcon, {
        onClick: this.handleDeleteNewVariation
      }),
      marginBlockEnd: "200",
      children: /*#__PURE__*/_jsx(FormControl, {
        "aria-label": "New variation name",
        error: !!error,
        validationMessage: /*#__PURE__*/_jsx(ErrorLabel, {
          error: error
        }),
        children: /*#__PURE__*/_jsx(TextInput, {
          onChange: this.handleNewVariationNameChange,
          value: name
        })
      })
    });
  }
  renderAddVariationButton() {
    if (!this.state.currentVariation.deleted || this.state.newVariation.name) {
      return null;
    }
    return /*#__PURE__*/_jsxs(Button, {
      onClick: this.handleAddVariation,
      use: "link",
      marginBlockEnd: "200",
      children: [/*#__PURE__*/_jsx(AddIcon, {}), /*#__PURE__*/_jsx(FormattedMessage, {
        message: "abTestManager.addVariation"
      })]
    }, "addVariationButton");
  }
  renderVariations() {
    return /*#__PURE__*/_jsx(FormControl, {
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "abTestManager.variationFields"
      }),
      children: /*#__PURE__*/_jsxs(List, {
        wrapChildren: true,
        children: [this.renderMasterVariationName(), this.renderCurrentVariation(), this.renderNewVariation(), this.renderAddVariationButton()]
      })
    });
  }
  renderABVariationDeleteModal() {
    if (!this.state.deleteModalOpen) {
      return null;
    }
    return /*#__PURE__*/_jsx(ConfirmModal, {
      confirmLabel: I18n.text('abTestManager.deleteModal.confirm'),
      confirmUse: "danger",
      disableConfirm: this.props.appStatus === APP_STATUS.LOADING,
      description: /*#__PURE__*/_jsx(FormattedMessage, {
        message: 'abTestManager.deleteModal.description'
      }),
      match: I18n.text('abTestManager.deleteModal.matcherValue'),
      matcherLabel: I18n.text('abTestManager.deleteModal.matcherLabel'),
      message: this.getMessage(),
      onConfirm: this.saveChanges,
      onReject: () => this.setState({
        deleteModalOpen: false
      }),
      rejectLabel: I18n.text('abTestManager.deleteModal.cancel'),
      use: "danger"
    });
  }
  renderSaveOrEndTestButton() {
    const {
      abMaster,
      categoryId,
      subcategory
    } = this.props;
    if (this.props.hasAbTestStarted) {
      return /*#__PURE__*/_jsx(Button, {
        use: "danger",
        href: ContentRoutes.details(abMaster.id, categoryId, subcategory, null, {
          tab: 'testResults'
        }),
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "abTestManager.endTestButton"
        })
      });
    }
    const {
      masterVariation,
      currentVariation,
      newVariation
    } = this.state;
    const saveDisabled = masterVariation.error || currentVariation.error || newVariation.error;
    return /*#__PURE__*/_jsx(Button, {
      disabled: saveDisabled,
      use: "primary",
      onClick: this.handleSaveChangesClick,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "abTestManager.confirmButton"
      })
    });
  }
  render() {
    const {
      onClose
    } = this.props;
    return /*#__PURE__*/_jsxs(Modal, {
      width: 450,
      children: [/*#__PURE__*/_jsxs(DialogHeader, {
        children: [/*#__PURE__*/_jsx(DialogCloseButton, {
          onClick: onClose
        }), /*#__PURE__*/_jsx(H2, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "abTestManager.header"
          })
        })]
      }), /*#__PURE__*/_jsxs(DialogBody, {
        children: [this.renderVariations(), this.renderABVariationDeleteModal()]
      }), /*#__PURE__*/_jsxs(DialogFooter, {
        children: [this.renderSaveOrEndTestButton(), /*#__PURE__*/_jsx(Button, {
          use: "secondary",
          onClick: onClose,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "abTestManager.cancel"
          })
        })]
      })]
    });
  }
}
ABTestManagerModalWithoutTheme.propTypes = {
  abMaster: PropTypes.instanceOf(ImmutableMap).isRequired,
  abVariation: PropTypes.instanceOf(ImmutableMap).isRequired,
  hasAbTestStarted: PropTypes.bool.isRequired,
  abTestId: PropTypes.number.isRequired,
  updatePagesAbTest: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  appStatus: PropTypes.string,
  subcategory: PropTypes.string.isRequired,
  categoryId: PropTypes.number.isRequired
};
const ABTestManagerModal = withTheme(ABTestManagerModalWithoutTheme);
export default connect(mapStateToProps, mapDispatchToProps)(ABTestManagerModal);